import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { FrVrfButton, FrVrfFooter } from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import { ApplicationMessageScreen } from "../../../messages";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { useGetRootComponents } from "../../../services/root-components";
import { createModal } from "../../modal";
import { Component } from "./ComponentSearchDialogState";
import { useComponentSearchDialogState } from "./useConponentSearchDialogState";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const ComponentSearchDialogContent = () => {
  const { showConfirmationDialog, showToast, resetUserError } =
    useGlobalNotifier();
  const [getRootComponentsResponse, sendGetRootComponents] =
    useGetRootComponents();
  const [dialogState, setDialogState] = useComponentSearchDialogState();
  const { moveToModalPage, closeModalPage } = usePageNavigation();
  const { messageForScreen } = useMessage();
  const COMPONENT_SEARCH_MODAL_TEMPLATE_FILE_URL =
    "spreadsheet-template/component-search-dialog.xlsx";

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<{
      componentList: Component[];
    }>();

  const spreadsheetInitialData = useMemo(() => {
    const componentList = dialogState.componentList;
    return {
      componentList: componentList,
    };
  }, [dialogState.componentList]);

  const onInitialize = () => {
    moveToModalPage();
    resetUserError();
    sendGetRootComponents({});
  };
  useEffect(onInitialize, []);

  const onGetRootComponentsResponse = () => {
    const res = getRootComponentsResponse?.rootCpntList ?? [];
    if (res.length === 0) {
      return;
    }
    const componentList = [] as Component[];
    res.map((rootCmp) => {
      rootCmp.cpntList.forEach((cmp) => {
        const component = {} as Component;
        component.isSelect = dialogState.selectedCpntId?.includes(cmp.cpntId)
          ? true
          : false;
        component.dipByRgn = rootCmp.dipByRgn ?? "";
        component.rootCpntId = rootCmp.rootCpntId ?? "";
        component.cpntId = cmp.cpntId ?? "";
        component.cpntName = cmp.cpntName ?? "";
        componentList.push(component);
      });
    });
    setDialogState((prevState) => ({
      ...prevState,
      componentList: componentList,
    }));
  };
  useEffect(onGetRootComponentsResponse, [getRootComponentsResponse]);

  const onCellEdited = (event: CustomEvent) => {
    const rowIndex = event.detail.cell.data.index as number;
    const isChecked = JSON.parse(event.detail.cell.value);
    const newComponentList = spreadsheetState?.componentList ?? [];
    if (newComponentList.length === 0) {
      return;
    }
    newComponentList[rowIndex]["isSelect"] = isChecked;
    setSpreadsheetState({
      ...spreadsheetState,
      componentList: newComponentList,
    });
    setDialogState((prevState) => ({
      ...prevState,
      isSaved: false,
    }));
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    const isChecked = JSON.parse(event.detail.cell.value);
    const newComponentList = dialogState.componentList ?? [];
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const rowIndex = item.data.index as number;
      newComponentList[rowIndex].isSelect = isChecked;
    });
    setSpreadsheetState({
      ...spreadsheetState,
      componentList: newComponentList,
    });
    setDialogState((prevState) => ({
      ...prevState,
      isSaved: false,
    }));
  };

  const onClickCancel = () => {
    if (dialogState.isSaved) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        closeModalPage();
        setDialogState((prevState) => ({
          ...prevState,
          isShow: false,
          isSaved: true,
        }));
      },
    });
  };

  const onClickApply = () => {
    const componentList = dialogState.componentList ?? [];
    if (componentList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    const cpntId = [] as string[];
    componentList.forEach((e) => {
      if (e.isSelect) {
        cpntId.push(e.cpntId);
      }
    });
    closeModalPage();
    setDialogState((prevState) => ({
      ...prevState,
      selectedCpntId: cpntId.length !== 0 ? cpntId : [],
      isShow: false,
      isSaved: true,
    }));
  };

  return (
    <>
      {createModal(
        "Search Component",
        <div style={{ height: "480px" }}>
          <FrVrfSpreadsheet
            data={spreadsheetInitialData}
            onInitialized={initSpreadsheetState}
            templateUrl={COMPONENT_SEARCH_MODAL_TEMPLATE_FILE_URL}
            onCellEdited={onCellEdited}
            onCellBulkEdited={onCellBulkEdited}
            height={400}
          />
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Apply"
              theme="primary"
              size="large"
              onClicked={onClickApply}
            />
          </FrVrfFooter>
        </div>,
        () => {},
        "medium",
      )}
    </>
  );
};
